import React, { FC, InputHTMLAttributes, ReactNode } from 'react';

import { isNil } from 'lodash-es';
import { useIntl } from 'react-intl';
import styled, { css } from 'styled-components';

import { useLoyaltyActiveCard } from 'hooks/loyalty';
import { useTabNavigationWatcher } from 'state/accessibility/use-tab-navigation-watcher.ts';
import noop from 'utils/noop';

import { Check } from '../icons/check';
import { RewardsStyleVariant, WithStyleVariant } from '../types';

import { CategoryTitle } from './category-title';

type RewardTierBaseProps = {
  name: string;
  points?: number;
  standalone?: boolean;
  tierLabel: ReactNode;
  titleId?: string;
} & WithStyleVariant;

export type RewardTierInputProps = RewardTierBaseProps & {
  allowTabNavigation?: boolean;
  ariaDescribedBy?: string;
  radioButtonName: string;
  selected: boolean;
  value: InputHTMLAttributes<HTMLInputElement>['value'];
};

type NotEnoughPointsProps = {
  hasEnoughPoints: boolean;
  message: string;
} & WithStyleVariant;

const Container = styled.div`
  display: flex;
  align-content: center;
  align-items: center;
`;

const TierLabel = styled.p<Pick<RewardTierBaseProps, 'standalone' | keyof WithStyleVariant>>`
  display: flex;
  position: relative;
  justify-content: center;
  align-items: center;
  top: 0;
  left: 0;
  font-size: 1.5rem;
  font-weight: ${Styles.fontWeight.heavy};
  margin: 0 1rem 0 0;
  line-height: 1px;
  color: ${({ variant }) =>
    variant === RewardsStyleVariant.Secondary ? Styles.color.white : Styles.color.primary};
  z-index: ${Styles.zIndex.below};
  font-family: ${Styles.fontFamily.base};
  height: 3rem;
`;

const CheckmarkWrapper = styled.span`
  display: flex;
  margin-left: auto;
  padding-left: 0.5em;
`;

const RewardsWrapper = styled.div`
  position: relative;
  height: 3rem;
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  font-family: ${Styles.fontFamily.base};
  color: ${Styles.color.grey.four};
`;

const Title = styled.p<WithStyleVariant>`
  max-width: 210px;
  ${Styles.mobileTiny`
    max-width: 160px;
  `}

  margin: 0px;
  flex-wrap: wrap;
  line-height: 1.3rem;
  font-size: 1.1rem;
  font-family: ${Styles.fontFamily.base};
  color: ${({ variant }) =>
    variant === RewardsStyleVariant.Secondary ? Styles.color.white : 'initial'};
`;

const Subtitle = styled(Title)`
  font-size: 0.75rem;
  padding-top: 0;
  color: ${({ variant }) =>
    variant === RewardsStyleVariant.Secondary ? Styles.color.white : Styles.color.grey.two};
`;

const radioButtonWidth = '24px';
const radioButtonHeight = radioButtonWidth;

const RadioButtonLabel = styled.label<WithStyleVariant>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${radioButtonWidth};
  height: ${radioButtonHeight};
  border-width: 1px;
  border-style: solid;
  border-color: ${({ variant }) =>
    variant === RewardsStyleVariant.Secondary ? Styles.color.white : Styles.color.grey.two};
  border-radius: 50%;
  background: ${({ variant }) =>
    variant === RewardsStyleVariant.Secondary ? '#990019' : Styles.color.white};
`;

const focusStyle = css`
  /* https://ghinda.net/article/mimic-native-focus-css/ */
  outline: 2px solid Highlight;
  @media (-webkit-min-device-pixel-ratio: 0) {
    outline: -webkit-focus-ring-color auto 5px;
  }
`;

const RadioButton = styled.input<WithStyleVariant & { isUsingTabNavigation: boolean }>`
  position: absolute;
  width: ${radioButtonWidth};
  height: ${radioButtonHeight};
  margin: 0;
  background: ${Styles.color.white};
  opacity: 0;
  z-index: ${Styles.zIndex.below};
  border-radius: 50%;
  cursor: pointer;

  &&:checked + label {
    background: ${({ variant }) =>
      variant === RewardsStyleVariant.Secondary ? Styles.color.white : Styles.color.primary};
    border-width: 1px;
    border-style: solid;
    border-color: ${({ variant }) =>
      variant === RewardsStyleVariant.Secondary ? Styles.color.white : Styles.color.grey.two};
  }
  &:focus ~ label {
    ${({ isUsingTabNavigation }) => isUsingTabNavigation && focusStyle}
  }
`;

const CheckContainer = styled.span`
  position: relative;
  top: 1px;
`;

const NotEnoughPointsMessage: FC<NotEnoughPointsProps> = ({
  hasEnoughPoints,
  message,
  variant,
}) => {
  if (!hasEnoughPoints) {
    return null;
  }

  return <Subtitle variant={variant}>{message}</Subtitle>;
};

export const RewardTierNoInput: FC<RewardTierBaseProps> = ({
  children,
  name = '',
  points,
  standalone = false,
  tierLabel,
  titleId,
  variant,
}) => {
  const { formatMessage } = useIntl();
  const { loyaltyDetails } = useLoyaltyActiveCard();
  const hasEnoughPoints = Boolean(
    points && loyaltyDetails && !isNil(loyaltyDetails.points) && loyaltyDetails.points < points
  );

  const TitleComponent = standalone ? CategoryTitle : Title;

  return (
    <Container>
      <RewardsWrapper>
        <TierLabel variant={variant} standalone={standalone}>
          {tierLabel}
        </TierLabel>
      </RewardsWrapper>
      <TitleWrapper>
        <TitleComponent id={titleId} variant={variant}>
          {name}
        </TitleComponent>
        <NotEnoughPointsMessage
          variant={variant}
          hasEnoughPoints={hasEnoughPoints}
          message={formatMessage({ id: 'notEnoughPoints' })}
        />
      </TitleWrapper>
      {children}
    </Container>
  );
};

export const RewardTierInput: FC<RewardTierInputProps> = ({
  allowTabNavigation = true,
  ariaDescribedBy,
  radioButtonName,
  selected,
  value,
  ...props
}) => {
  const { isUsingTabNavigation } = useTabNavigationWatcher();
  const titleId = `reward-tier-title-${value}`;

  return (
    <RewardTierNoInput {...props} titleId={titleId}>
      <CheckmarkWrapper>
        <RadioButton
          aria-labelledby={titleId}
          aria-describedby={ariaDescribedBy}
          checked={selected}
          id={`${radioButtonName}-${value}`}
          isUsingTabNavigation={isUsingTabNavigation}
          name={radioButtonName}
          onChange={noop} // prevents React console error -- event is handled by parent onClick
          tabIndex={allowTabNavigation ? 0 : -1}
          type="radio"
          value={value}
          variant={props.variant}
        />
        <RadioButtonLabel variant={props.variant}>
          <CheckContainer>
            <Check
              fillColor={
                props.variant === RewardsStyleVariant.Secondary ? '#990019' : Styles.color.white
              }
            />
          </CheckContainer>
        </RadioButtonLabel>
      </CheckmarkWrapper>
    </RewardTierNoInput>
  );
};
